import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Icon,
	IconButton,
	MenuItem,
	TextField,
	Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ArrowBackIosOutlined, ArrowForwardIosOutlined } from '@material-ui/icons'
import { KeyboardDatePicker } from '@material-ui/pickers'
import clsx from 'clsx'
import moment from 'moment'
import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { showError, showSuccess } from '../../../actions/misc/misc.actions'
import { api } from '../../../services/api'
import DeleteConfirmation from '../DeleteConfirmation'
import { filterMembershipTypesForHousehold } from '../../../utils/membershipTypes'

const useStyles = makeStyles((theme) => ({
	dialogActions: {
		justifyContent: 'flex-start'
	},
	grid: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
		width: '60%'
	},
	gridDouble: {
		display: 'grid',
		gridGap: '23px',
		marginBottom: '23px',
		gridTemplateColumns: '1fr 1fr',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				width: '100%',
				maxHeight: '70px'
			}
		}
	},
	fieldWrapper: {
		'& .MuiTextField-root': { minWidth: '378px' }
	},
	summaryHeader: {
		fontSize: '12px',
		color: '#1A1A35',
		fontWeight: '800',
		opacity: '.75'
	},
	text: {
		color: '#1A1A35',
		fontSize: '14px'
	},
	col1: {
		width: '55%'
	},
	col2: {
		width: '20%'
	},
	col3: {
		width: '25%',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-end'
	},
	statusIcon: {
		fontSize: '12px',
		lineHeight: '12px',
		height: '12px',
		marginRight: '5px'
	}
}))

const DEFAULT_ENTRY_STATE = {
	charge_type: 'Charge Account',
	access_type: '',
	number: '1',
	start_date: moment().add(1, 'day').toDate()
}

const AddMembershipDialog = (props) => {
	const dispatch = useDispatch()
	const classes = useStyles()

	const { upcoming, client: household, currentMembership, isShown, setIsShown, addNewMembership } = props
	const membershipTypes = useSelector((state) => state.specific.households.membershipTypes)
	const facility = useSelector((state) => state.specific.facilities.selected)

	const [types, setTypes] = useState([])
	const [entry, setEntry] = useState(DEFAULT_ENTRY_STATE)
	const [appliedPromo, setAppliedPromo] = useState(null)
	const [hasPromo, addPromo] = useState(false)
	const [promo, setPromo] = useState('')
	const [promoLoading, setPromoLoading] = useState(false)
	const [discountApplied, applyDiscount] = useState(false)
	const [discount, setDiscount] = useState(0)
	const [accountCharged, setAccountCharged] = useState(false)
	const [cards, setCards] = useState([])
	const [cardsLoaded, setCardsLoaded] = useState(false)
	const [isStartDateOpen, setIsStartDateOpen] = useState(false)
	const [minDate, setMinDate] = useState(moment().toDate())

	const LOWER_DISCOUNT_LIMIT = 0
	const HIGHER_DISCOUNT_LIMIT = 100

	const calculateTotal = () => {
		if (!entry._id) return null

		let add_price = types.find((m) => m._id === entry._id)?.additional_pet_fee
		let price = types.find((m) => m._id === entry._id)?.price

		let built_in_disc = types.find((m) => m._id === entry._id)?.discount

		let newNum = entry.number - 1
		let add = add_price * newNum
		let total = price + add

		if (built_in_disc) {
			total -= total * built_in_disc
		}

		if (discount && discountApplied) {
			let disc = discount / 100
			let off = total * disc
			total = total - off
		}

		return total
	}

	const promoAmount = useMemo(() => {
		if (!appliedPromo) return 0

		let total = calculateTotal()

		let { max_discount, value, unit } = appliedPromo.promo

		if (unit === 'cents') return `$${(value / 100).toFixed(2)}`
		if (unit === 'percent') {
			let discount = total * (value / 100)
			if (max_discount && discount > max_discount) return max_discount
			return `$${(discount / 100).toFixed(2)}`
		}

		return 0
	}, [entry, appliedPromo])

	useEffect(() => {
		if (membershipTypes && membershipTypes.length && household) {
			let typesInMarket = filterMembershipTypesForHousehold(membershipTypes, household).filter(
				(t) => t.status !== 'inactive'
			)

			setTypes(typesInMarket)
		}
	}, [household, membershipTypes, currentMembership, upcoming])

	const formatAndAddNewMembership = (entry) => {
		let membership = {
			type: entry.access_type,
			num_of_dogs: entry.number,
			promo_id: appliedPromo?.promo?._id,
			charge_type: entry.charge_type,
			transaction_id: entry.transaction_id,
			discount: discount && discountApplied ? discount : 0,
			membership_id: entry._id
		}

		if (upcoming) {
			membership.start_date = entry.start_date
			membership.upcoming = true
		}

		addNewMembership(membership)
		setEntry(DEFAULT_ENTRY_STATE)
		setAppliedPromo(null)
	}

	useEffect(() => {
		if (currentMembership && !currentMembership.auto_renew && isShown) {
			setEntry({
				...entry,
				start_date: moment(currentMembership.end_date).add(1, 'day').toDate()
			})

			setMinDate(moment(currentMembership.end_date).toDate())
		} else {
			setMinDate(moment().toDate())
		}
	}, [currentMembership, isShown])

	const applyPromo = async () => {
		try {
			setPromoLoading(true)
			let { data } = await api.post(`/checkMemberPromo`, {
				promo,
				household_id: household._id,
				service_target: 'memberships',
				membership_id: entry._id
			})
			if (data?.errors?.length || data.status === 'invalid') {
				dispatch(showError(data.errors[0] || 'Invalid promo'))
				setPromoLoading(false)
				return
			}
			dispatch(showSuccess('Promo Applied!'))

			setAppliedPromo(data.data[0])
			setPromoLoading(false)
		} catch (e) {
			console.log('big ol e...', e)
			dispatch(showError('Promo not found'))
			setPromoLoading(false)
		}
	}

	const isValidMembership = (membership) => {
		if (!membership.number || !membership._id) {
			return false
		}
		if (
			(upcoming && moment(entry.start_date).isSame(moment(minDate), 'day')) ||
			!moment(entry.start_date).hour(12).isAfter(moment(minDate).hour(11))
		) {
			return false
		}
		if (entry.charge_type !== 'Charge Account') {
			return false
		}
		return true
	}

	const getEntryMethod = () => {
		if (!entry?._id) return '-'
		const found = types.find((m) => m._id === entry?._id)

		return found?.name
	}

	const getPrice = () => {
		if (!entry._id) return '-'
		let price = types.find((m) => m._id === entry._id)?.price

		return `$${price / 100}`
	}

	const getDiscount = () => {
		if (!entry._id) return '-'
		let price = types.find((m) => m._id === entry._id)?.price
		let discount = types.find((m) => m._id === entry._id)?.discount
		if (!price || !discount) return '-'

		let amt = price * discount

		return `-$${amt / 100}`
	}

	const getAdditionalPetPrice = () => {
		if (!entry._id) return '-'
		let price = types.find((m) => m._id === entry._id)?.additional_pet_fee

		return `$${price / 100}`
	}

	const getTotal = () => {
		if (!entry._id) return '-'

		let total = calculateTotal()

		if (appliedPromo?.promo) {
			let { max_discount, value, unit } = appliedPromo.promo

			if (unit === 'cents') {
				total -= value
			} else {
				let discount = total * (value / 100)
				if (max_discount && discount > max_discount) discount = max_discount
				total -= discount
			}
		}

		return `$${(total / 100).toFixed(2)}`
	}

	const doValidations = () => {
		formatAndAddNewMembership(entry)
		setIsShown(false)
	}

	const currentType = useMemo(() => {
		if (!entry._id) return null
		let type = types.find((m) => m._id === entry._id)
		return type
	}, [entry, types])

	const validateDiscount = (e) => {
		const current_discount = e?.target?.value || 0
		if (current_discount < LOWER_DISCOUNT_LIMIT || current_discount > HIGHER_DISCOUNT_LIMIT) return
		if (!isNaN(e.target.value)) setDiscount(e.target.value)
	}

	const getPromoBlock = () => {
		return (
			<div
				style={{
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'flex-start'
				}}>
				<div
					style={{
						width: '100%',
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginBottom: 16
					}}>
					<TextField
						variant="filled"
						value={discount || ''}
						label="Discount %"
						onChange={(e) => validateDiscount(e)}
					/>
					<Button
						variant="outlined"
						disabled={!discount}
						onClick={applyDiscount}
						style={{
							fontFamily: 'Nunito',
							color: '#6B4AD6',
							borderWidth: '#6B4AD6',
							borderColor: '#6B4AD6',
							height: 40,
							paddingRight: 12,
							paddingLeft: 12
						}}>
						APPLY
					</Button>
				</div>
				{!appliedPromo ? (
					<div
						style={{
							width: '100%',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: 24
						}}>
						<TextField
							variant="filled"
							value={promo || ''}
							label="Promo Code"
							onChange={(e) => {
								setPromo(e.target.value)
							}}
						/>
						<Button
							variant="outlined"
							disabled={!promo || !entry._id}
							onClick={applyPromo}
							style={{
								fontFamily: 'Nunito',
								color: '#6B4AD6',
								borderWidth: '#6B4AD6',
								borderColor: '#6B4AD6',
								height: 40,
								paddingRight: 12,
								paddingLeft: 12,
								opacity: !promo || !entry._id ? 0.5 : 1
							}}>
							{promoLoading ? <CircularProgress /> : 'Apply'}
						</Button>
					</div>
				) : null}
			</div>
		)
	}

	const getSummary = () => {
		let has_discount = types.find((m) => m._id === entry._id)?.discount
		const hasMembershipTypeDiscount = has_discount && has_discount > 0

		return (
			<div
				style={{
					width: '285px',
					backgroundColor: '#F9F9F9',
					borderRadius: 6,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'flex-start',
					alignItems: 'flex-start',
					padding: 16
				}}>
				<div
					style={{
						width: '100%',
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'flex-start',
						border: `0px 0px 1px 0px solid #F3F3F3`,
						paddingBottom: 16,
						marginBottom: 16
					}}>
					<Typography
						variant="body2"
						style={{
							fontWeight: '800',
							fontSize: 16,
							color: '#1A1A35'
						}}>
						Summary
					</Typography>
				</div>
				<div
					style={{
						width: '100%',
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'flex-start',
						paddingBottom: 8
					}}>
					<div className={classes.col1}>
						<Typography
							variant="body2"
							style={{
								fontWeight: '800',
								fontSize: 12,
								letterSpacing: 0.75,
								opacity: 0.75,
								color: '#1A1A35'
							}}>
							ITEM
						</Typography>
					</div>
					<div className={classes.col2}>
						<Typography
							variant="body2"
							style={{
								fontWeight: '800',
								fontSize: 12,
								letterSpacing: 0.75,
								opacity: 0.75,
								color: '#1A1A35'
							}}>
							QTY
						</Typography>
					</div>
					<div className={classes.col3}>
						<Typography
							variant="body2"
							style={{
								fontWeight: '800',
								fontSize: 12,
								letterSpacing: 0.75,
								opacity: 0.75,
								color: '#1A1A35'
							}}>
							COST
						</Typography>
					</div>
				</div>
				<div
					style={{
						width: '100%',
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'flex-start'
					}}>
					<div className={classes.col1}>
						<Typography
							variant="body2"
							style={{
								fontSize: 14,
								color: '#1A1A35',
								lineHeight: '24px'
							}}>
							{getEntryMethod()}
						</Typography>
					</div>
					<div className={classes.col2}>
						<Typography
							variant="body2"
							style={{
								fontSize: 14,
								color: '#1A1A35',
								lineHeight: '24px'
							}}>
							1x
						</Typography>
					</div>
					<div className={classes.col3}>
						<Typography
							variant="body2"
							style={{
								fontSize: 14,
								color: '#1A1A35',
								lineHeight: '24px'
							}}>
							{getPrice()}
						</Typography>
					</div>
				</div>
				{entry.number > 1 ? (
					<div
						style={{
							width: '100%',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-start'
						}}>
						<div className={classes.col1}>
							<Typography
								variant="body2"
								style={{
									fontSize: 14,
									color: '#1A1A35',
									lineHeight: '24px'
								}}>
								Additional Pets
							</Typography>
						</div>
						<div className={classes.col2}>
							<Typography
								variant="body2"
								style={{
									fontSize: 14,
									color: '#1A1A35',
									lineHeight: '24px'
								}}>
								{entry.number - 1}x
							</Typography>
						</div>
						<div className={classes.col3}>
							<Typography
								variant="body2"
								style={{
									fontSize: 14,
									color: '#1A1A35',
									lineHeight: '24px'
								}}>
								{getAdditionalPetPrice()}
							</Typography>
						</div>
					</div>
				) : null}

				{!hasMembershipTypeDiscount ? null : (
					<div
						style={{
							width: '100%',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-start'
						}}>
						<div className={classes.col1}>
							<Typography
								variant="body2"
								style={{
									fontSize: 14,
									color: '#1A1A35',
									lineHeight: '24px'
								}}>
								Default Discount
							</Typography>
						</div>
						<div className={classes.col2}>
							<Typography
								variant="body2"
								style={{
									fontSize: 14,
									color: '#1A1A35',
									lineHeight: '24px'
								}}>
								1x
							</Typography>
						</div>
						<div className={classes.col3}>
							<Typography
								variant="body2"
								style={{
									fontSize: 14,
									color: '#1A1A35',
									lineHeight: '24px'
								}}>
								{getDiscount()}
							</Typography>
						</div>
					</div>
				)}

				{!appliedPromo ? null : (
					<div
						style={{
							paddingTop: 8,
							paddingBottom: 8,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'flex-start',
							alignItems: 'flex-start',
							width: '100%'
						}}>
						<Typography
							variant="body2"
							style={{
								fontSize: 14,
								color: '#1A1A35',
								fontStyle: 'italic',
								lineHeight: '24px'
							}}>
							Applied Promo:
						</Typography>
						<div
							style={{
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'space-between',
								alignItems: 'center',
								width: '100%'
							}}>
							<Typography
								variant="body2"
								style={{
									fontSize: 14,
									color: '#1A1A35',
									fontStyle: 'italic',
									lineHeight: '24px'
								}}>
								{appliedPromo?.promo?.code?.toUpperCase()}
							</Typography>
							<Typography
								variant="body2"
								style={{
									fontSize: 14,
									color: '#1A1A35',
									lineHeight: '24px'
								}}>
								-{promoAmount}
							</Typography>
						</div>
						<Button
							variant="text"
							onClick={() => {
								setAppliedPromo(null)
								setEntry({ ...entry, promoCode: '' })
							}}
							style={{ paddingLeft: 0, paddingTop: 0 }}>
							<Typography
								variant="body2"
								style={{
									fontSize: 14,
									color: '#1A1A35',
									textDecoration: 'underline',
									lineHeight: '24px'
								}}>
								Remove
							</Typography>
						</Button>
					</div>
				)}
				{!discountApplied ? null : (
					<div
						style={{
							paddingTop: 8,
							paddingBottom: 8,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'flex-start',
							alignItems: 'flex-start'
						}}>
						<Typography
							variant="body2"
							style={{
								fontSize: 14,
								color: '#1A1A35',
								fontStyle: 'italic',
								lineHeight: '24px'
							}}>
							Applied Discount:
						</Typography>
						<Typography
							variant="body2"
							style={{
								fontSize: 14,
								color: '#1A1A35',
								fontStyle: 'italic',
								lineHeight: '24px'
							}}>
							- {discount}%
						</Typography>
						<Button
							variant="text"
							onClick={() => applyDiscount(false)}
							style={{ paddingLeft: 0, paddingTop: 0 }}>
							<Typography
								variant="body2"
								style={{
									fontSize: 14,
									color: '#1A1A35',
									textDecoration: 'underline',
									lineHeight: '24px'
								}}>
								Remove
							</Typography>
						</Button>
					</div>
				)}
				<div
					style={{
						width: '100%',
						paddingTop: 8,
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'flex-start'
					}}>
					<Typography
						variant="body2"
						style={{
							fontSize: 16,
							fontWeight: '600',
							color: '#1A1A35',
							lineHeight: '22px'
						}}>
						Total
					</Typography>
					<Typography
						variant="body2"
						style={{
							fontSize: 16,
							fontWeight: '600',
							color: '#1A1A35',
							lineHeight: '22px'
						}}>
						{getTotal()}
					</Typography>
				</div>
			</div>
		)
	}

	const StartDatePicker = () => {
		return (
			<KeyboardDatePicker
				className={classes.datePicker}
				disableToolbar
				InputProps={{
					style: {
						minWidth: '400px',
						height: '55px'
					}
				}}
				style={{
					margin: 0,
					marginBottom: 16,
					width: '400px',
					minWidth: '400px'
				}}
				error={
					moment(entry.start_date).isSame(moment(minDate), 'day') ||
						!moment(entry.start_date).isAfter(moment(minDate))
						? true
						: false
				}
				InputAdornmentProps={{
					position: 'start',
					component: () => (
						<IconButton onClick={() => setIsStartDateOpen(true)} style={{ paddingTop: 30 }}>
							<Icon
								className="far fa-calendar-alt"
								style={{
									fontSize: '1.325rem'
								}}
							/>
						</IconButton>
					)
				}}
				open={isStartDateOpen}
				onOpen={() => setIsStartDateOpen(true)}
				onClose={() => setIsStartDateOpen(false)}
				variant="inline"
				format="MM/DD/YYYY"
				margin="normal"
				id="start-date"
				label="Start Date"
				inputVariant="filled"
				value={moment(entry.start_date)}
				onChange={(val) => {
					setEntry({ ...entry, start_date: val.toDate() })
				}}
				rightArrowIcon={<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />}
				leftArrowIcon={<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />}
				KeyboardButtonProps={{
					'aria-label': 'Start Date',
					className: classes.calendarButton
				}}
			/>
		)
	}

	return (
		<Dialog
			open={isShown}
			className={classes.dialog}
			onClose={() => {
				setIsShown(false)
				setCardsLoaded(false)
				setCards([])
				setEntry(DEFAULT_ENTRY_STATE)
				setPromo(null)
				setAppliedPromo(false)
				setDiscount(null)
				applyDiscount(false)
				setAccountCharged(false)
			}}
			PaperProps={{
				style: {
					width: 920,
					maxWidth: 920,
					minHeight: 500,
					maxHeight: 700,
					overflowY: 'scroll'
				}
			}}>
			<DialogTitle
				style={{
					marginBottom: 24
				}}>
				{upcoming ? 'ADD UPCOMING MEMBERSHIP' : 'ADD ACTIVE MEMBERSHIP'}
			</DialogTitle>
			<DialogContent>
				<div
					style={{
						width: '100%'
					}}>
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between'
						}}>
						<div className={classes.grid}>
							<TextField
								variant="filled"
								value={entry.charge_type || ''}
								label="Payment Type"
								select
								style={{ width: '100%', marginBottom: 16 }}
								onChange={(e) => {
									setEntry({ ...entry, charge_type: e.target.value })
								}}>
								{['Charge Account']
									.filter((t) => {
										if (upcoming) return t === 'Charge Account'
										return true
									})
									.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
							</TextField>

							<TextField
								variant="filled"
								select
								value={entry._id || ''}
								label="Access Type"
								style={{
									marginBottom: 16,
									minWidth: 240,
									width: '50%'
								}}
								InputProps={{
									style: {
										width: '50%',
										minWidth: 240
									}
								}}
								onChange={(e) => {
									setEntry({ ...entry, _id: e.target.value })
								}}>
								{types.map((option) => (
									<MenuItem
										key={option._id}
										value={option._id}
										style={{ textTransform: 'capitalize' }}>
										{option.name} {option.billing_method ? `(${option.billing_method})` : ''}
									</MenuItem>
								))}
							</TextField>
							<TextField
								variant="filled"
								select
								value={entry.number || ''}
								style={{ marginBottom: 16, minWidth: 240 }}
								disabled={currentType?.grandfathered_pricing}
								InputProps={{
									style: {
										width: '50%',
										minWidth: 240
									}
								}}
								label="Number of Dogs"
								onChange={(e) => setEntry({ ...entry, number: e.target.value })}>
								{[1, 2, 3, 4].map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</TextField>
							{upcoming && <StartDatePicker />}
							{entry.charge_type === 'Point of Sale' ? (
								<TextField
									variant="filled"
									value={entry.transaction_id || ''}
									style={{ marginBottom: 16, minWidth: 240, width: '100%' }}
									InputProps={{
										style: {
											width: '100%',
											minWidth: 240
										}
									}}
									label="Transaction ID"
									onChange={(e) => setEntry({ ...entry, transaction_id: e.target.value })}
								/>
							) : null}
							{!hasPromo ? (
								<Button
									variant="text"
									onClick={() => addPromo(true)}
									style={{ color: '#6B4AD6', paddingLeft: 0 }}>
									ADD PROMO OR DISCOUNT
								</Button>
							) : (
								getPromoBlock()
							)}
						</div>
						<div className={classes.receipt}>{getSummary()}</div>
					</div>
				</div>
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button
					variant="contained"
					onClick={doValidations}
					color="secondary"
					disabled={!isValidMembership(entry)}>
					Confirm
				</Button>
				<Button variant="outlined" onClick={() => setIsShown(false)} color="secondary">
					CANCEL
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default AddMembershipDialog
